import { Check, Info, Plus, Warning, X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { Checkbox, InputNumber, Select } from 'antd';
import type { InputNumberProps } from 'antd';
import { usePlaidLink } from 'react-plaid-link';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import { DEPOSIT_LIMITS_IN_CENTS } from '../../../../../../../types/apiTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import {
  formatCurrencyForAntd,
  parseCurrencyFromAntd,
} from '../../../../../../../utils/currencyFormat';
import {
  newDepositIdempotencyKey,
  useDepositCustomerId,
  useDepositMutation,
  useLinkedAccountsQuery,
} from '../../hooks/useDeposits';
import {
  DEPOSIT_CONSENT_VERSION,
  buildDepositConsentText,
} from './depositConsent';

const MIN_DOLLARS = DEPOSIT_LIMITS_IN_CENTS.minPerTransaction / 100;
const MAX_DOLLARS = DEPOSIT_LIMITS_IN_CENTS.maxPerTransaction / 100;

const formatUsd = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatUsdWhole = (n: number): string => `$${n.toLocaleString('en-US')}`;

type SubmitError = { message: string };

export const DepositModal = ({
  isOpen,
  onClose,
  internalBankAccountId,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** The customer's TACH internal bank account (the deposit destination). */
  internalBankAccountId: string;
}) => {
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const customerId = useDepositCustomerId();

  const accountsQuery = useLinkedAccountsQuery(customerId, isOpen);
  const depositMutation = useDepositMutation(customerId);
  const accounts = useMemo(
    () => accountsQuery.data ?? [],
    [accountsQuery.data]
  );

  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  // A broken bank connection (Plaid ITEM_LOGIN_REQUIRED) blocks deposits from THIS account until it's
  // reconnected. Kept separate from `submitError` (which is transient) so toggling the amount/consent
  // can't clear it — only a successful reconnect or switching to a different account clears it. While
  // set, the Deposit button is disabled (see `canSubmit`).
  const [reauthNeeded, setReauthNeeded] = useState(false);

  // Plaid "link a new account" flow (kept from the original scaffold).
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [openLink, setOpenLink] = useState(false);

  const selectedAccount = accounts.find(
    (a) => a.account_id === selectedAccountId
  );
  const amountCents = Math.round((amount ?? 0) * 100);
  const amountInRange =
    amountCents >= DEPOSIT_LIMITS_IN_CENTS.minPerTransaction &&
    amountCents <= DEPOSIT_LIMITS_IN_CENTS.maxPerTransaction;

  // The exact authorization text shown to (and sent from) the customer.
  const consentText = useMemo(() => {
    if (!selectedAccount || amount == null || !amountInRange) return '';
    return buildDepositConsentText({
      amountFormatted: formatUsd(amount),
      bankName: selectedAccount.bank_name ?? 'bank',
      bankMask: selectedAccount.bank_mask,
    });
  }, [selectedAccount, amount, amountInRange]);

  // `internalBankAccountId` is intentionally NOT gated here: if it's missing we still want the button
  // clickable so `onProceed`'s guard can explain why (a disabled button would be silently dead).
  const canSubmit =
    !!selectedAccount?.deposit_eligible &&
    amountInRange &&
    consentChecked &&
    !reauthNeeded &&
    !depositMutation.isPending;

  // Auto-select the first eligible account (prefer the primary) once accounts load.
  useEffect(() => {
    if (selectedAccountId || accounts.length === 0) return;
    const eligible = accounts.filter((a) => a.deposit_eligible);
    const primary = eligible.find((a) => a.is_primary) ?? eligible[0];
    if (primary) setSelectedAccountId(primary.account_id);
  }, [accounts, selectedAccountId]);

  // Fetch a Plaid link token so "Link New Bank Account" is ready.
  useEffect(() => {
    const fetchLinkToken = async () => {
      setLoadingToken(true);
      try {
        const response = await callProtectedApi(
          '/linkedAccounts/plaidLinkToken',
          'POST',
          { owner_id: customerId, entity: 'customer' },
          'Error occurred while connecting to Plaid.',
          {},
          undefined,
          5
        );
        if (response?.data?.data?.token) setLinkToken(response.data.data.token);
      } catch (error) {
        // Acquiring the token can throw (e.g. an Auth0 silent-auth failure) — swallow it so it's not
        // an unhandled rejection. The "Link New Bank Account" button just stays disabled (no token).
        console.error('Error fetching Plaid link token:', error);
      } finally {
        setLoadingToken(false);
      }
    };
    if (customerId) fetchLinkToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const onPlaidSuccess = useCallback(
    async (public_token: string) => {
      try {
        const response = await callProtectedApi(
          `/linkedAccounts/${customerId}`,
          'post',
          { entity: 'customer', public_token },
          'Error occurred while creating newly linked account record.'
        );
        if (response?.status === HttpStatusCode.Created) {
          // Refresh the (eligibility-enriched) account list so the new account appears.
          queryClient.invalidateQueries({
            queryKey: ['linkedAccounts', customerId],
          });
        } else {
          toast.error('Failed to save newly linked account.');
        }
      } catch (error) {
        // The token acquisition inside callProtectedApi can throw; without this catch a just-linked
        // account would silently never save. Tell the user so they can retry.
        console.error('Error saving newly linked account:', error);
        toast.error('Failed to save your linked account. Please try again.');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken!,
    onSuccess: onPlaidSuccess,
  });

  useEffect(() => {
    if (openLink && ready) {
      open();
      setOpenLink(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLink, ready, open]);

  // ── Reauth (Plaid update mode) ──────────────────────────────────────────────
  // Repairs a BROKEN connection in place (no duplicate account) — a separate Link instance + token
  // from the add-account flow above. The reauth token is minted on-demand (30-min expiry) for the
  // specific account; its access_token stays server-side.
  const [updateLinkToken, setUpdateLinkToken] = useState<string | null>(null);
  const [openUpdateLink, setOpenUpdateLink] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const onReauthSuccess = useCallback(
    () => {
      // Item repaired — refresh eligibility and clear the error so the customer can re-tap Deposit.
      queryClient.invalidateQueries({
        queryKey: ['linkedAccounts', customerId],
      });
      setReauthNeeded(false);
      setSubmitError(null);
      toast.success('Bank reconnected. Please try your deposit again.', {
        duration: 5000,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId]
  );

  const { open: openUpdate, ready: readyUpdate } = usePlaidLink({
    token: updateLinkToken!,
    onSuccess: onReauthSuccess,
  });

  useEffect(() => {
    if (openUpdateLink && readyUpdate) {
      openUpdate();
      setOpenUpdateLink(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openUpdateLink, readyUpdate, openUpdate]);

  // Mint an update-mode token for the selected account, then launch Link in update mode.
  const handleReconnect = async () => {
    if (!selectedAccountId) return;
    setReconnecting(true);
    try {
      const response = await callProtectedApi(
        '/linkedAccounts/plaidReauthToken',
        'POST',
        {
          owner_id: customerId,
          entity: 'customer',
          linked_account_id: selectedAccountId,
        },
        'Error occurred while reconnecting your bank.',
        {},
        undefined,
        5
      );
      const token = response?.data?.data?.token;
      if (token) {
        setUpdateLinkToken(token);
        setOpenUpdateLink(true);
      } else {
        toast.error('Could not start bank reconnection. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching Plaid reauth token:', error);
      toast.error('Could not start bank reconnection. Please try again.');
    } finally {
      setReconnecting(false);
    }
  };

  const handleAmountChange: InputNumberProps['onChange'] = (value) => {
    // Cap at cents — dollar amounts have at most 2 decimal places (mirrors WithdrawModal's clamp).
    setAmount(value == null ? null : Number(Number(value).toFixed(2)));
    setSubmitError(null);
    // The consent checkbox authorizes a SPECIFIC amount — changing it invalidates that affirmative
    // authorization, so force a fresh check (the displayed consent text recomputes to match).
    setConsentChecked(false);
  };

  const handleOutcome = (
    outcome: Awaited<ReturnType<typeof depositMutation.mutateAsync>>
  ) => {
    switch (outcome.status) {
      case 'success':
        toast.success('Transfer successfully initiated.', { duration: 5000 });
        onClose();
        // Land on the transactions feed so the customer immediately sees the new pending deposit in
        // the Processing band. The mutation already invalidated ['pendingTransactions'], so the band
        // fetches fresh when it mounts on this route.
        navigate('/dashboard/banking/transactions');
        return;
      case 'limit_exceeded':
        setSubmitError({
          message: outcome.message ?? 'That amount exceeds your deposit limit.',
        });
        return;
      case 'resource_locked':
        setSubmitError({
          message:
            'You already have a deposit in progress. Please try again in a moment.',
        });
        return;
      case 'deposits_disabled':
        setSubmitError({
          message:
            'Deposits are temporarily unavailable. Please try again later.',
        });
        return;
      case 'insufficient_balance':
        setSubmitError({
          message:
            outcome.message ??
            'That account doesn’t have enough available funds.',
        });
        return;
      case 'account_not_debit_capable': {
        // Record (not a ternary) so adding a reason to DepositIneligibleReason forces a message here.
        const messageByReason: Record<typeof outcome.reason, string> = {
          not_linked:
            'That account was added manually and can’t be used for deposits — only accounts linked via Plaid can deposit into TACH.',
          no_auth_accounts:
            'This bank connection doesn’t have a checking or savings account we can use for deposits. Please choose or link a checking or savings account.',
          debit_disabled:
            'This account isn’t available for deposits. Contact support or use a different account.',
        };
        setSubmitError({ message: messageByReason[outcome.reason] });
        return;
      }
      case 'reauth_required':
        // Block Deposit for this account until they reconnect or switch accounts (see `reauthNeeded`).
        setSubmitError(null);
        setReauthNeeded(true);
        return;
    }
  };

  const onProceed = async () => {
    setSubmitError(null);
    if (!selectedAccount?.deposit_eligible) {
      setSubmitError({ message: 'Choose an eligible bank account.' });
      return;
    }
    if (!amountInRange) {
      setSubmitError({
        message: `Enter an amount between ${formatUsdWhole(MIN_DOLLARS)} and ${formatUsdWhole(MAX_DOLLARS)}.`,
      });
      return;
    }
    if (!consentChecked) {
      setSubmitError({ message: 'Please authorize the transfer to continue.' });
      return;
    }
    if (!internalBankAccountId) {
      setSubmitError({
        message: 'Your TACH account isn’t ready yet. Please try again shortly.',
      });
      return;
    }
    try {
      const outcome = await depositMutation.mutateAsync({
        linkedAccountId: selectedAccount.account_id,
        internalBankAccountId,
        amountInCents: amountCents,
        idempotencyKey: newDepositIdempotencyKey(), // fresh key per attempt
        authorization: {
          consent_version: DEPOSIT_CONSENT_VERSION,
          consent_text: consentText,
        },
      });
      handleOutcome(outcome);
    } catch {
      setSubmitError({
        message:
          'Something went wrong starting your deposit. Please try again.',
      });
    }
  };

  if (!isOpen) return null;

  const accountOptions = accounts.map((a) => ({
    value: a.account_id,
    disabled: !a.deposit_eligible,
    // Label shows in both the dropdown and the closed selector (mirrors WithdrawModal): the primary
    // eligible account gets the green "Primary" check; ineligible accounts show why instead.
    label: (
      <div className="flex w-full flex-row items-center justify-between">
        <span>
          {a.bank_name ?? 'Bank'} ****{a.bank_mask}
        </span>
        {a.deposit_eligible && a.is_primary ? (
          <span className="flex items-center gap-1 text-green-600">
            <Check size={16} />
            <span className="text-[12px] font-semibold">Primary</span>
          </span>
        ) : !a.deposit_eligible ? (
          // `not_linked` = a manually-added account (no Plaid token) — it can't ACH-pull, and
          // "re-link" is wrong (it was never Plaid-linked). The helper line below explains why.
          <span className="text-[12px] text-graysecondary">
            {a.deposit_ineligible_reason === 'debit_disabled'
              ? 'Unavailable'
              : 'Not eligible'}
          </span>
        ) : null}
      </div>
    ),
  }));

  const hasEligible = accounts.some((a) => a.deposit_eligible);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="max-h-[92%] w-[95%] max-w-[520px] overflow-y-auto overflow-x-hidden rounded-[4px] bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
          <h2 className="text-[16px] font-[600] text-primary">
            Deposit into TACH
          </h2>
          <X
            className="hover:cursor-pointer hover:opacity-80"
            onClick={onClose}
            weight="regular"
            size={24}
          />
        </div>

        {accountsQuery.isLoading ? (
          <div className="flex h-[240px] w-full items-center justify-center">
            <ClipLoader color="#1D213B" size={48} aria-label="Loading" />
          </div>
        ) : accountsQuery.isError ? (
          // A FAILED fetch must not look like "No eligible accounts" (an empty-but-loaded state) —
          // show an explicit error + Retry so the customer knows to try again, not that they qualify
          // for nothing.
          <div className="flex h-[240px] w-full flex-col items-center justify-center gap-3 border-y border-bordergray p-6 text-center">
            <Warning size={28} className="text-danger" weight="fill" />
            <p className="text-[13px] text-graysecondary">
              We couldn’t load your linked accounts. Please try again.
            </p>
            <button
              type="button"
              onClick={() => accountsQuery.refetch()}
              className="rounded-full border border-bordergray px-4 py-[6px] text-[13px] font-semibold text-primary hover:bg-primarybg"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-4 border-y border-bordergray p-6">
            {/* Source account */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                From bank account
              </label>
              <Select
                value={selectedAccountId || undefined}
                onChange={(value) => {
                  setSelectedAccountId(value);
                  setSubmitError(null);
                  // Switching accounts clears the reauth block — a different account may be healthy,
                  // and the broken one is no longer selected.
                  setReauthNeeded(false);
                  // Consent authorizes a SPECIFIC source account — re-affirm on change (see amount).
                  setConsentChecked(false);
                }}
                options={accountOptions}
                placeholder={
                  hasEligible ? 'Select an account' : 'No eligible accounts'
                }
                className="w-full"
              />
              <p className="mt-1 flex items-start gap-1 text-[12px] text-graysecondary">
                <Info size={14} className="mt-[1px] shrink-0" />
                Only accounts linked via Plaid can be used to deposit into TACH.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setOpenLink(true)}
              disabled={!linkToken}
              className="flex h-[32px] w-full flex-row items-center justify-center space-x-2 rounded-full border border-[#212529] border-opacity-[8%] px-3 py-[6px] align-middle disabled:opacity-50"
            >
              <Plus />
              <p>Link New Bank Account</p>
              {loadingToken && (
                <ClipLoader color="#1D213B" size={16} aria-label="Loading" />
              )}
            </button>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Amount
              </label>
              <InputNumber
                min={0}
                max={MAX_DOLLARS}
                controls={false}
                value={amount}
                onChange={handleAmountChange}
                formatter={(v) => formatCurrencyForAntd(v)}
                parser={(v) => parseCurrencyFromAntd(v)}
                className="w-full"
              />
              <p className="mt-1 text-[12px] text-graysecondary">
                Min {formatUsdWhole(MIN_DOLLARS)} · Max{' '}
                {formatUsdWhole(MAX_DOLLARS)} per deposit
              </p>
            </div>

            {/* Consent */}
            {consentText && (
              <label className="flex cursor-pointer items-start gap-2 rounded-[4px] bg-primarybg p-3">
                <Checkbox
                  checked={consentChecked}
                  onChange={(e) => {
                    setConsentChecked(e.target.checked);
                    setSubmitError(null);
                  }}
                  className="mt-[2px]"
                />
                <span className="text-[12px] leading-snug text-graysecondary">
                  {consentText}
                </span>
              </label>
            )}

            {/* Reauth CTA — a broken bank connection blocks this account. Deposit stays disabled (via
                `reauthNeeded` in `canSubmit`) until they reconnect or pick a different account. */}
            {reauthNeeded && (
              <div className="flex flex-col gap-3 rounded-[4px] bg-bgred p-3">
                <div className="flex items-start gap-2">
                  <Warning
                    size={18}
                    className="mt-[1px] shrink-0 text-danger"
                    weight="fill"
                  />
                  <p className="text-[13px] text-danger">
                    Your bank connection needs to be refreshed before you can
                    deposit from this account. Reconnect it, or choose a
                    different account.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleReconnect}
                  disabled={!selectedAccountId || reconnecting}
                  className="inline-flex h-[40px] items-center justify-center gap-2 self-start rounded-full bg-primary px-5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {reconnecting ? (
                    <>
                      <ClipLoader
                        color="#ffffff"
                        size={14}
                        aria-label="Loading"
                      />
                      Reconnecting…
                    </>
                  ) : (
                    'Reconnect your bank'
                  )}
                </button>
              </div>
            )}

            {/* Transient submit error (limit, insufficient balance, etc.) */}
            {submitError && (
              <div className="flex items-start gap-2 rounded-[4px] bg-bgred p-3">
                <Warning
                  size={18}
                  className="mt-[1px] shrink-0 text-danger"
                  weight="fill"
                />
                <p className="text-[13px] text-danger">{submitError.message}</p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between gap-2 p-4">
          <NeutralButton
            onClick={onClose}
            addClass="rounded-full w-full h-[48px]"
          >
            Cancel
          </NeutralButton>
          <AccentButton
            disabled={!canSubmit}
            onClick={onProceed}
            addClass="rounded-full w-full h-[48px] disabled:opacity-50"
          >
            {depositMutation.isPending ? (
              <ClipLoader color="#1D213B" size={20} aria-label="Loading" />
            ) : (
              'Deposit'
            )}
          </AccentButton>
        </div>
      </div>
    </div>
  );
};

export default DepositModal;
